import axios from "axios";
import isLocalDev from "./privyLocalDev";

/**
 * Shared transport for the KOL campaigns / agency features
 * (KOL_CAMPAIGNS_AGENCY_PLAN.md). Same conventions as user.actions.js:
 * ppapi = POST formData with the action as a key + `hash` + `urlUsed`;
 * walletless = REST with `X-Partner-Hash`.
 *
 * Agency "viewing as": when an agency account is looking at a linked KOL,
 * every READ call carries `viewPartnerId`. ppapi/walletless enforce the
 * accepted link server-side; write actions reject it.
 */

export const PPAPI_URL = "https://ppapi.instaswap.com/formData";
// On localhost the CRA dev proxy (src/setupProxy.js) forwards /api/walletless same-origin.
export const WALLETLESS_URL = isLocalDev ? "/api/walletless" : "https://walletlessapi.instaswap.com";

export const VIEW_PARTNER_KEY = "agencyViewPartner"; // JSON { id, name, hash }
export const VIEW_PARTNER_EVENT = "agency-view-changed";

// The view is bound to the SESSION that set it: after a logout / a different
// account signing in on the same browser the stale entry is ignored (and
// dropped), so a non-agency account never sends a foreign viewPartnerId.
function sessionHash() {
  try { return localStorage.getItem("hash") || ""; } catch { return ""; }
}

export function getViewPartner() {
  try {
    const raw = localStorage.getItem(VIEW_PARTNER_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || !(Number(v.id) > 0)) return null;
    if (!v.hash || v.hash !== sessionHash()) { localStorage.removeItem(VIEW_PARTNER_KEY); return null; }
    return { id: Number(v.id), name: v.name || `#${v.id}` };
  } catch {
    return null;
  }
}

export function setViewPartner(partner) {
  try {
    if (partner && Number(partner.id) > 0) {
      localStorage.setItem(VIEW_PARTNER_KEY, JSON.stringify({ id: Number(partner.id), name: partner.name || "", hash: sessionHash() }));
    } else {
      localStorage.removeItem(VIEW_PARTNER_KEY);
    }
  } catch { /* private mode */ }
  try { window.dispatchEvent(new Event(VIEW_PARTNER_EVENT)); } catch { /* ssr */ }
}

/** Add `viewPartnerId` to a READ payload when the agency is viewing a partner. */
export function withView(payload) {
  const v = getViewPartner();
  return v ? { ...payload, viewPartnerId: v.id } : payload;
}

/**
 * POST one ppapi action. Resolves with `response.data.response`.
 * Rejects with the axios error (see apiErrorMessage).
 */
export async function ppapiPost(action, params = {}, { view = false } = {}) {
  const base = {
    [action]: 1,
    ...params,
    urlUsed: window.location.href,
    hash: localStorage.getItem("hash"),
  };
  const payload = view ? withView(base) : base;
  const res = await axios.post(PPAPI_URL, payload);
  const body = res.data?.response;
  if (body && typeof body === "object" && body.error) {
    const err = new Error(body.error);
    err.response = { status: res.status, data: res.data };
    throw err;
  }
  return body;
}

/** GET a walletless partner endpoint (X-Partner-Hash). Resolves with `res.data`. */
export async function walletlessGet(path, params = {}, { view = true } = {}) {
  const q = view ? withView(params) : params;
  const res = await axios.get(`${WALLETLESS_URL}${path}`, {
    params: q,
    headers: { "X-Partner-Hash": localStorage.getItem("hash") || "" },
  });
  return res.data;
}

/** Human message from a ppapi / walletless error. */
export function apiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const status = error?.response?.status;
  const d = error?.response?.data;
  const msg =
    (typeof d?.response === "string" && d.response) ||
    (typeof d?.response?.error === "string" && d.response.error) ||
    (typeof d?.error === "string" && d.error) ||
    (typeof error?.message === "string" && !/^Request failed/i.test(error.message) && error.message) ||
    null;
  if (status === 403) return msg || "Not allowed for this account.";
  if (status === 450) return "Your session has expired — please sign in again.";
  return msg || fallback;
}

/**
 * Human message for a failed registration (privyRegister / registerPartner).
 * ppapi reports the reason in several shapes: `response` as a string,
 * `response.error`, top-level `error`/`message`, or nothing at all (e.g. the
 * partnerName UNIQUE constraint) — name that last case explicitly, since
 * "try again" would just fail again.
 */
export function registrationErrorMessage(body) {
  const r = body?.response;
  const raw =
    (typeof r === "string" && r) ||
    (typeof r?.error === "string" && r.error) ||
    (typeof r?.message === "string" && r.message) ||
    (typeof body?.error === "string" && body.error) ||
    (typeof body?.message === "string" && body.message) ||
    "";
  if (raw && !/^(false|0|error)$/i.test(raw)) return raw;
  if (/duplicate|unique/i.test(raw)) return "That company name is already taken — please choose a different one.";
  return "Could not create the account. If you picked a company name, it may already be taken — try a different one.";
}

/** yyyy-mm-dd (date input) → ISO date-time the backends expect. */
export const dateInputToIso = (value) => (value ? `${value}T00:00:00Z` : undefined);
/** End of the picked day — walletless compares `created_at <= to`, so a bare date would drop that day. */
export const dateInputToIsoEnd = (value) => (value ? `${value}T23:59:59Z` : undefined);
/** Inclusive `{from,to}` for the stats endpoints. */
export const rangeParams = (from, to) => ({ from: dateInputToIso(from), to: dateInputToIsoEnd(to) });
export const toDateInputValue = (date) => date.toISOString().slice(0, 10);

export function defaultRange(days = 30) {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

export const fmtUsd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);
export const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Math.round(Number(n) || 0));

/** Trigger a browser download of text content. */
export function downloadText(filename, text, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
